import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@/utils/apiParamsUtils';

/**
 * Creates a new user with the provided data.
 * @param {Object} payload - The data for the new user.
 * @returns {Promise<Object>} A promise that resolves with the created user data.
 * @throws Will throw an error if the creation request fails.
 */
export const createNewUser = async (payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.post('/users/create', payload, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to create a new user:', error);
    throw error;
  }
};

/**
 * Retrieves all users.
 * @returns {Promise<Object[]>} A promise that resolves with an array of user objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllUsers = async (params) => {
  const filters = prepareFilters(params, {
    city: 'default',
    country: 'default',
    createdAt: 'range',
    updatedAt: 'range',
    email: 'default',
    firstName: 'default',
    lastName: 'default',
    status: 'uppercaseArray',
    roles: 'default',
    companies: 'array',
  });

  const sorters = prepareSorters(params, {
    firstName: 'sortFirstName',
    lastName: 'sortLastName',
    status: 'sortStatus',
    companies: 'sortCompanies',
    createdAt: 'sortCreatedAt',
    updatedAt: 'sortUpdatedAt',
    country: 'sortCountry',
    city: 'sortCity',
  });

  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/users', {
      params: {
        filter: filters,
        sorter: sorters,
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve users:', error);
    throw error;
  }
};

/**
 * Retrieves dropdown all users.
 * @returns {Promise<Object[]>} A promise that resolves with an array of user objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveDropdownAllUsers = async (params) => {
  try {
    const response = await axiosClient.get('/users/dropdown', {
      params: {
        filter: params?.filter || {},
        select: params?.select || {},
        type: params?.type || '',
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }
        if (params.type) {
          params.type = JSON.stringify(params.type);
        }
        if (params.select) {
          params.select = JSON.stringify(params.select);
        }
        return qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve users:', error);
    throw error;
  }
};

/**
 * Retrieves a user by their ID.
 * @param {string} userId - The unique identifier of the user.
 * @returns {Promise<Object>} A promise that resolves with the user data.
 * @throws Will throw an error if the request fails.
 */
export const getUserById = async (userId) => {
  try {
    const response = await axiosClient.get(`/users/${userId}`);
    const { data } = response.data;
    return data;
  } catch (error) {
    console.error(`Failed to retrieve user with ID ${userId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing user by their ID with the provided data.
 * @param {string} userId - The unique identifier of the user.
 * @param {Object} payload - The updated data for the user.
 * @returns {Promise<Object>} A promise that resolves with the updated user data.
 * @throws Will throw an error if the update request fails.
 */
export const updateUserById = async (userId, payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.put(`/users/${userId}`, payload, {
      headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error(`Failed to update user with ID ${userId}:`, error);
    throw error;
  }
};

/**
 * Updates multiple users with the provided data.
 * @param {Array<{ payload: Object }>} payload - An array of update objects, each containing a user ID and corresponding data to update.
 * @returns {Promise<Array<Object>>} A promise that resolves with the updated user data for all users.
 * @throws Will throw an error if any update request fails.
 */
export const updateUsersInBulk = async (payload) => {
  try {
    const response = await axiosClient.post(`/users/bulk-update`, payload);
    return response.data;
  } catch (error) {
    console.error(`Failed to update user with ID ${payload}:`, error);
    throw error;
  }
};

/**
 * Deletes a user by their ID.
 * @param {string} userId - The unique identifier of the user.
 * @returns {Promise<Object>} A promise that resolves when the user is deleted.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteUserById = async (userId) => {
  try {
    const response = await axiosClient.delete(`/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete user with ID ${userId}:`, error);
    throw error;
  }
};

/**
 * Deletes multiple users based on the provided IDs.
 * @param {Array<string>} userIds - An array of user IDs to delete.
 * @returns {Promise<Object>} A promise that resolves with the response data from the bulk delete request.
 * @throws Will throw an error if the bulk delete request fails.
 */
export const deleteUsersInBulk = async (userIds) => {
  try {
    const response = await axiosClient.post('/users/bulk-delete', { userIds });
    return response.data;
  } catch (error) {
    console.error(`Failed to delete users with IDs ${userIds}:`, error);
    throw error;
  }
};

/**
 * Sends bulk invitation emails to users.
 * @param {Array<string>} userIds - An array of user IDs to send invitations to.
 * @returns {Promise<Object>} A promise that resolves with the result of the bulk invitation request.
 * @throws Will throw an error if the bulk invitation request fails.
 */
export const sendBulkInvitations = async (userIds) => {
  try {
    const response = await axiosClient.post('/users/send-bulk-invitations', {
      userIds,
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to send invitations to users:`, error);
    throw error;
  }
};

/**
 * Sends custom email to users with optional attachments.
 * @param {Object} data - The email data.
 * @param {Array<string>} data.userIds - An array of user IDs to send email to.
 * @param {string} data.subject - Email subject.
 * @param {string} data.body - Email body content.
 * @param {Array<File>} data.attachments - Optional array of files to attach.
 * @returns {Promise<Object>} A promise that resolves with the result of the send request.
 * @throws Will throw an error if the send request fails.
 */
export const sendCustomEmail = async ({ userIds, subject, body, attachments = [] }) => {
  try {
    const formData = new FormData();
    formData.append('userIds', JSON.stringify(userIds));
    formData.append('subject', subject);
    formData.append('body', body);

    attachments.forEach((file) => {
      formData.append('attachments', file);
    });

    const response = await axiosClient.post('/users/send-custom-email', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to send custom email to users:`, error);
    throw error;
  }
};

const UsersService = {
  createNewUser,
  retrieveAllUsers,
  retrieveDropdownAllUsers,
  getUserById,
  updateUserById,
  updateUsersInBulk,
  deleteUserById,
  deleteUsersInBulk,
  sendBulkInvitations,
  sendCustomEmail,
};

export default UsersService;

/**
 * The signed-in person's arrangement of a table — column order and hidden
 * columns. `table` is `incomingJournal` or `outgoingJournal`.
 */
export const getTablePreference = async (table) => {
  const response = await axiosClient.get(`/users/me/table-preferences/${table}`);

  return response.data?.data || { order: [], hidden: [] };
};

export const saveTablePreference = async (table, { order, hidden }) => {
  const response = await axiosClient.put(`/users/me/table-preferences/${table}`, {
    order,
    hidden,
  });

  return response.data?.data;
};

export const resetTablePreference = async (table) => {
  const response = await axiosClient.delete(`/users/me/table-preferences/${table}`);

  return response.data?.data;
};
